import axios, { type AxiosError } from 'axios';
import type { ApiResponse, Paginated } from '@pmc/shared';

/**
 * API client.
 *
 * Two things every call depends on:
 *   withCredentials — the PID session is an HttpOnly cookie; without this the
 *                     visitor loses their located property on every request.
 *   envelope unwrap — the server always replies `{ ok, data }` or `{ ok, error }`,
 *                     so callers get `data` directly and failures throw an
 *                     `ApiFailureError` carrying the field-level messages.
 */

const rawBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'https://api-pmc-ptax.innoproduction.in/citizen/api/';
const baseURL = rawBaseUrl.endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`;

const client = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
});

export class ApiFailureError extends Error {
  readonly code: string;
  readonly fields: Record<string, string>;
  readonly status: number;

  constructor(code: string, message: string, fields: Record<string, string>, status: number) {
    super(message);
    this.name = 'ApiFailureError';
    this.code = code;
    this.fields = fields;
    this.status = status;
  }
}

/** The language the user picked, sent on every request. */
let currentLanguage = 'en';

export function setApiLanguage(lang: string): void {
  currentLanguage = lang;
}

client.interceptors.request.use((config) => {
  config.params = { ...(config.params as object), lang: currentLanguage };
  return config;
});

function unwrap<T>(payload: any, status: number): T {
  if (!payload || typeof payload !== 'object') return payload as T;
  if (payload.ok === true || payload.success === true) {
    return (payload.data !== undefined ? payload.data : payload) as T;
  }
  if (payload.ok === undefined && payload.success === undefined) {
    return payload as T;
  }
  const errorObj = payload.error || {};
  const code = errorObj.code || (payload.success === false ? 'API_ERROR' : 'UNKNOWN_ERROR');
  const message = errorObj.message || payload.message || 'An error occurred';
  const fields = errorObj.fields || {};
  throw new ApiFailureError(code, message, fields, status);
}

async function request<T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  body?: unknown,
  config?: { params?: Record<string, unknown>; headers?: Record<string, string> },
): Promise<T> {
  try {
    let cleanUrl = url;
    if (cleanUrl.startsWith('/api/')) {
      cleanUrl = cleanUrl.slice(5);
    } else if (cleanUrl.startsWith('api/')) {
      cleanUrl = cleanUrl.slice(4);
    } else if (cleanUrl.startsWith('/')) {
      cleanUrl = cleanUrl.slice(1);
    }
    const response = await client.request<any>({
      method,
      url: cleanUrl,
      ...(body !== undefined ? { data: body } : {}),
      ...(config ?? {}),
    });
    return unwrap<T>(response.data, response.status);
  } catch (err) {
    if (err instanceof ApiFailureError) throw err;

    const axiosError = err as AxiosError<any>;
    const payload = axiosError.response?.data;

    if (payload && typeof payload === 'object' && (payload.ok === false || payload.success === false)) {
      const errorObj = payload.error || {};
      throw new ApiFailureError(
        errorObj.code || 'API_ERROR',
        errorObj.message || payload.message || 'Request failed',
        errorObj.fields ?? {},
        axiosError.response?.status ?? 0,
      );
    }

    // Network failure, timeout, or a non-envelope response (a proxy error page).
    throw new ApiFailureError(
      'NETWORK_ERROR',
      'Could not reach the server. Please check your connection and try again.',
      {},
      axiosError.response?.status ?? 0,
    );
  }
}

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    request<T>('get', url, undefined, params ? { params } : undefined),
  post: <T>(url: string, body?: unknown) => request<T>('post', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('put', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('patch', url, body),
  delete: <T>(url: string) => request<T>('delete', url),

  /** Multipart upload — axios sets the boundary, so no Content-Type here. */
  upload: <T>(url: string, file: File, fieldName = 'file') => {
    const form = new FormData();
    form.append(fieldName, file);
    return request<T>('put', url, form);
  },

  /** Absolute URL for a streamed document, for <img> and <iframe> sources. */
  fileUrl: (path: string) => {
    const cleanBase = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
    return path.startsWith('/api') ? path.replace(/^\/api/, cleanBase) : `${cleanBase}${path.startsWith('/') ? '' : '/'}${path}`;
  },
};

export type { Paginated };
